import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IosShare from '@mui/icons-material/IosShare';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import BottomSheet from './BottomSheet';
import DrilldownAboutSection from './DrilldownAboutSection';
import DrilldownSectionHeader from './DrilldownSectionHeader';
import METRIC_DEFINITIONS from './metricDefinitions';
import { ANALYTICS_HEADLINE_NUMBER_SX } from './analyticsTypography';
import { ActivityRow } from './MetricDrillModal';
import MetricScopeDrilldownNote from './MetricScopeDrilldownNote';
import { fetchAllOccurrencesForEvent } from './analyticsTimeseriesFetch';
import { SHARE_EVENT_KEYS } from './shareEvents';
import { buildFilteredSharingRows } from './sharingActivityRows';
import { mergeFeedRows } from './visitorSegregation';
import { buildSignupUserDirectory } from './accountCreatedEnrichment';
import { useGetAllUsersQuery } from '../../Slices/roomNateApis';

const PAGE_SIZE = 12;

const NO_AUTO_CAPS = {
  textTransform: 'none',
  '& .MuiTypography-root': { textTransform: 'none' },
  '& span': { textTransform: 'none' },
};

/** Compact KPI tile — icon header, hero count, period subtext (no chart). */
const SharingMetricCard = ({
  title,
  icon,
  count,
  periodSubtitle,
  onClick,
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      ...NO_AUTO_CAPS,
      position: 'relative',
      width: '100%',
      height: '100%',
      textAlign: 'left',
      display: 'block',
      borderRadius: '14px !important',
      border: '1px solid rgba(0,0,0,0.06)',
      bgcolor: '#fff',
      p: { xs: 1.75, sm: 2 },
      transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' },
      '&:active': { transform: 'scale(0.997)' },
      cursor: 'pointer',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: { xs: 1, sm: 1.25 } }}>
      {icon}
      <Typography
        sx={{
          fontSize: { xs: '0.875rem', md: '0.9375rem' },
          fontWeight: 500,
          color: 'rgba(0,0,0,0.5)',
        }}
      >
        {title}
      </Typography>
    </Stack>

    <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
      {count.toLocaleString()}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: '0.75rem', md: '0.8125rem' },
        fontWeight: 400,
        color: 'rgba(0,0,0,0.45)',
        mt: 0.3,
        lineHeight: 1.4,
      }}
    >
      {periodSubtitle}
    </Typography>

    <ChevronRight
      sx={{
        position: 'absolute',
        top: { xs: 15, sm: 19 },
        right: { xs: 11, sm: 15 },
        fontSize: 18,
        color: 'rgba(0,0,0,0.3)',
        opacity: 0.45,
        pointerEvents: 'none',
      }}
    />
  </ButtonBase>
);

/**
 * SharingActivityCard
 *
 * KPI tiles and drill-down sheets for shares (link_copied + legacy
 * link_shared) and download_layout. Counts and recent-event lists share one
 * source: a full-window /timeseries walk (then client-side demand-scope +
 * @magicfurnish.com filters).
 */
const SharingActivityCard = ({
  dateLabel = '',
  dateParams = '',
  partnerNameFilter = '',
  usersPartnerFilter = null,
  drilldownFeedRows = [],
  contextRows = [],
  excludeInternal = false,
  excludePartnerAgents = false,
  partnerAgentEmailDomain = null,
  partnerAdminUsers = [],
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [shareListOpen, setShareListOpen] = useState(true);
  const [downloadListOpen, setDownloadListOpen] = useState(true);
  const [shareVisibleCount, setShareVisibleCount] = useState(PAGE_SIZE);
  const [downloadVisibleCount, setDownloadVisibleCount] = useState(PAGE_SIZE);
  const sheetOpen = shareOpen || downloadOpen;
  const [activityRowsRaw, setActivityRowsRaw] = useState({
    share: [],
    download_layout: [],
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const windowReqRef = useRef(0);

  const periodSubtitle = dateLabel
    ? `in the ${dateLabel.toLowerCase()}`
    : 'in this period';

  const scopeFilterOpts = useMemo(
    () => ({
      excludeInternal,
      excludeAgentActivity: excludePartnerAgents,
      partnerHostname: partnerNameFilter,
      partnerAdminUsers,
      contextRows: mergeFeedRows(
        contextRows,
        activityRowsRaw.share,
        activityRowsRaw.download_layout
      ),
    }),
    [
      excludeInternal,
      excludePartnerAgents,
      partnerNameFilter,
      partnerAdminUsers,
      contextRows,
      activityRowsRaw.share,
      activityRowsRaw.download_layout,
    ]
  );

  const shareRows = useMemo(
    () => buildFilteredSharingRows(activityRowsRaw.share, scopeFilterOpts),
    [activityRowsRaw.share, scopeFilterOpts]
  );

  const downloadRows = useMemo(
    () => buildFilteredSharingRows(activityRowsRaw.download_layout, scopeFilterOpts),
    [activityRowsRaw.download_layout, scopeFilterOpts]
  );

  const shareCount = shareRows.length;
  const downloadCount = downloadRows.length;

  useEffect(() => {
    if (!dateParams || !partnerNameFilter) {
      setActivityRowsRaw({ share: [], download_layout: [] });
      setMetricsLoading(false);
      return undefined;
    }

    const reqId = ++windowReqRef.current;
    setMetricsLoading(true);

    Promise.all([
      fetchAllOccurrencesForEvent(SHARE_EVENT_KEYS, dateParams, partnerNameFilter),
      fetchAllOccurrencesForEvent('download_layout', dateParams, partnerNameFilter),
    ])
      .then(([shares, downloads]) => {
        if (reqId !== windowReqRef.current) return;
        setActivityRowsRaw({
          share: Array.isArray(shares) ? shares : [],
          download_layout: Array.isArray(downloads) ? downloads : [],
        });
      })
      .catch(() => {
        if (reqId !== windowReqRef.current) return;
        setActivityRowsRaw({ share: [], download_layout: [] });
      })
      .finally(() => {
        if (reqId === windowReqRef.current) setMetricsLoading(false);
      });

    return () => {
      windowReqRef.current += 1;
    };
  }, [dateParams, partnerNameFilter]);

  useEffect(() => {
    if (!sheetOpen) {
      setShareVisibleCount(PAGE_SIZE);
      setDownloadVisibleCount(PAGE_SIZE);
      setShareListOpen(true);
      setDownloadListOpen(true);
    }
  }, [sheetOpen]);

  const allRowsForStats = useMemo(() => {
    return [
      ...(Array.isArray(drilldownFeedRows) ? drilldownFeedRows : []),
      ...shareRows,
      ...downloadRows,
    ];
  }, [drilldownFeedRows, shareRows, downloadRows]);

  const usersQueryArg =
    !usersPartnerFilter || usersPartnerFilter === 'all' ? null : usersPartnerFilter;
  const { data: signupUsersRaw } = useGetAllUsersQuery(usersQueryArg, { skip: !sheetOpen });
  const signupUserDirectory = useMemo(
    () => buildSignupUserDirectory(signupUsersRaw),
    [signupUsersRaw]
  );

  const visibleShareRows = shareRows.slice(0, shareVisibleCount);
  const visibleDownloadRows = downloadRows.slice(0, downloadVisibleCount);
  const hasMoreShares = shareVisibleCount < shareRows.length;
  const hasMoreDownloads = downloadVisibleCount < downloadRows.length;

  const renderEventSection = ({
    title,
    visibleRows,
    hasMore,
    onLoadMore,
    emptyMessage,
    isLoading,
    countLabel,
    listOpen,
    onToggleList,
  }) => (
    <Box sx={{ mb: 2.5 }}>
      <DrilldownSectionHeader
        title={title}
        countLabel={countLabel}
        collapsible
        open={listOpen}
        onToggle={onToggleList}
      />
      <Collapse in={listOpen}>
        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: '#fff',
            overflow: 'hidden',
          }}
        >
          {visibleRows.length === 0 ? (
            <Typography
              sx={{ px: 2, py: 2, fontSize: '0.8rem', color: 'rgba(0,0,0,0.45)' }}
            >
              {isLoading ? 'Loading…' : emptyMessage}
            </Typography>
          ) : (
            visibleRows.map((row, idx) => (
              <ActivityRow
                key={row._id || `${row.timestamp}-${row?.meta?.event}-${idx}`}
                row={row}
                idx={idx}
                eventKey={row?.meta?.event}
                allRows={allRowsForStats}
                signupUserDirectory={signupUserDirectory}
              />
            ))
          )}
          {hasMore && (
            <ButtonBase
              onClick={onLoadMore}
              sx={{
                width: '100%',
                py: 1.25,
                borderTop: '1px solid rgba(0,0,0,0.06)',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'rgba(99,102,241,0.9)',
                borderRadius: '0 0 12px 12px !important',
                textTransform: 'none !important',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
              }}
            >
              Load more
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </ButtonBase>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  const sharesDownloadsGridSx = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: { xs: 1.5, sm: 2 },
    width: '100%',
    minWidth: 0,
  };

  if (metricsLoading) {
    return (
      <Box sx={sharesDownloadsGridSx}>
        <Skeleton
          variant="rounded"
          height={120}
          sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
        />
        <Skeleton
          variant="rounded"
          height={120}
          sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box sx={sharesDownloadsGridSx}>
        <SharingMetricCard
          title="Shares"
          icon={<IosShare sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />}
          count={shareCount}
          periodSubtitle={periodSubtitle}
          onClick={() => setShareOpen(true)}
        />
        <SharingMetricCard
          title="Downloads"
          icon={
            <FileDownloadOutlinedIcon
              sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }}
            />
          }
          count={downloadCount}
          periodSubtitle={periodSubtitle}
          onClick={() => setDownloadOpen(true)}
        />
      </Box>

      <BottomSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Shares"
        subtitle={dateLabel || undefined}
      >
        <MetricScopeDrilldownNote
          scope="demand"
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
        />
        {METRIC_DEFINITIONS.shares && (
          <DrilldownAboutSection
            body={METRIC_DEFINITIONS.shares.body}
            sections={METRIC_DEFINITIONS.shares.sections}
            sheetOpen={shareOpen}
          />
        )}
        {renderEventSection({
          title: 'Recent shares',
          visibleRows: visibleShareRows,
          hasMore: hasMoreShares,
          onLoadMore: () => setShareVisibleCount((n) => n + PAGE_SIZE),
          emptyMessage: 'No shares in this period',
          isLoading: metricsLoading && shareRows.length === 0,
          countLabel: `${visibleShareRows.length} of ${shareRows.length} shown`,
          listOpen: shareListOpen,
          onToggleList: () => setShareListOpen((v) => !v),
        })}
      </BottomSheet>

      <BottomSheet
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        title="Downloads"
        subtitle={dateLabel || undefined}
      >
        <MetricScopeDrilldownNote
          scope="demand"
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
        />
        {METRIC_DEFINITIONS.exports && (
          <DrilldownAboutSection
            body={METRIC_DEFINITIONS.exports.body}
            sections={METRIC_DEFINITIONS.exports.sections}
            sheetOpen={downloadOpen}
          />
        )}
        {renderEventSection({
          title: 'Recent downloads',
          visibleRows: visibleDownloadRows,
          hasMore: hasMoreDownloads,
          onLoadMore: () => setDownloadVisibleCount((n) => n + PAGE_SIZE),
          emptyMessage: 'No downloads in this period',
          isLoading: metricsLoading && downloadRows.length === 0,
          countLabel: `${visibleDownloadRows.length} of ${downloadRows.length} shown`,
          listOpen: downloadListOpen,
          onToggleList: () => setDownloadListOpen((v) => !v),
        })}
      </BottomSheet>
    </>
  );
};

export default SharingActivityCard;
